import { API_ENDPOINTS } from './config';

import { APP_ROUTES } from '@/shared/routes/paths';
import type { User } from '@/shared/types/user';

export class OrganizationAuthorizationRequiredError extends Error {
  constructor(message?: string) {
    super(message);
    this.name = 'OrganizationAuthorizationRequiredError';
  }
}

export const initiateLogin = (options?: { redirectUrl?: string }): void => {
  const url = new URL(API_ENDPOINTS.AUTH_LOGIN);

  // Always send `state`. The API falls back to the bare portal origin when it is
  // absent, which lands on the public marketing page instead of the app.
  url.searchParams.append('state', options?.redirectUrl ?? APP_ROUTES.ROOT);

  window.location.href = url.toString();
};

export const logout = async (): Promise<void> => {
  try {
    const url = new URL(API_ENDPOINTS.AUTH_LOGOUT);

    await fetch(url.toString(), {
      method: 'POST',
      credentials: 'include'
    });
  } catch (error) {
    console.warn('Logout API call failed (likely due to redirect):', error);
  }
};

export const switchOrganization = async (
  organizationId: string
): Promise<User> => {
  const response = await fetch(API_ENDPOINTS.AUTH_SWITCH_ORGANIZATION, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ organizationId })
  });

  if (!response.ok) {
    const error = await response.json();

    // Check if user needs full authentication for this org
    if (error.error?.type === 'organization_authorization_required') {
      initiateLogin({
        redirectUrl: window.location.pathname + window.location.search
      });
      throw new OrganizationAuthorizationRequiredError(error.error.message);
    }

    throw new Error('Failed to switch organization');
  }

  const data = await response.json();
  return data.data.user;
};
